import { convertToRaw, EditorState, Modifier, RichUtils } from "draft-js";
import { useMemo, useState, useCallback } from "react";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import { ColorPicker, type ColorPickerProps as AntColorPickerProps } from "antd";
import { BgColorsOutlined } from "@ant-design/icons";

import "./Mentions.scss";
import { ISelectOption } from "shared/models";

type Props = {
  onChange?: (content: string, mentionIds: string[]) => void;
  placeHolder?: string;
  options?: ISelectOption[];
};

// SVG icon data URLs matching Ant Design icon style
const boldIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="64 64 896 896" width="16" height="16"><path fill="#000" d="M697.8 481.4c33.6-35 54.2-82.3 54.2-134.3v-10.2C752 229.3 663.9 142 555.3 142H259.4c-15.1 0-27.4 12.3-27.4 27.4v679.1c0 16.3 13.2 29.5 29.5 29.5h318.7c117 0 211.8-94.2 211.8-210.5v-11c0-73-37.4-137.3-94.2-175.1zM328 238h224.7c57.1 0 103.3 44.4 103.3 99.3v9.5c0 54.8-46.3 99.3-103.3 99.3H328V238zm366.6 429.4c0 62.9-51.7 113.9-115.5 113.9H328V542.7h251.1c63.8 0 115.5 51 115.5 113.9v10.8z"/></svg>')}`;
const italicIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="64 64 896 896" width="16" height="16"><path fill="#000" d="M798 160H366c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h181.2l-156 544H229c-4.4 0-8 3.6-8 8v64c0 4.4 3.6 8 8 8h432c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8H474.4l156-544H798c4.4 0 8-3.6 8-8v-64c0-4.4-3.6-8-8-8z"/></svg>')}`;
const underlineIcon = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="64 64 896 896" width="16" height="16"><path fill="#000" d="M824 804H200c-4.4 0-8 3.4-8 7.6v60.8c0 4.2 3.6 7.6 8 7.6h624c4.4 0 8-3.4 8-7.6v-60.8c0-4.2-3.6-7.6-8-7.6zm-312-76c69.4 0 134.6-27.1 183.8-76.2C745 602.7 772 537.4 772 468V156c0-6.6-5.4-12-12-12h-60c-6.6 0-12 5.4-12 12v312c0 97-79 176-176 176s-176-79-176-176V156c0-6.6-5.4-12-12-12h-60c-6.6 0-12 5.4-12 12v312c0 69.4 27.1 134.6 76.2 183.8C377.3 701 442.6 728 512 728z"/></svg>')}`;

// Color presets for the color picker
const colorPresets: AntColorPickerProps["presets"] = [
  {
    label: "Colors",
    colors: [
      "#000000", "#434343", "#666666", "#999999", "#B7B7B7",
      "#D9D9D9", "#FFFFFF", "#FF4D4F", "#FF7A45", "#FFA940",
      "#FFC53D", "#FFEC3D", "#BAE637", "#73D13D", "#36CFC9",
      "#40A9FF", "#597EF7", "#9254DE", "#F759AB",
    ],
  },
];

// Custom ColorPicker component using Ant Design's ColorPicker (renders in portal)
type ColorPickerComponentProps = {
  editorState: EditorState;
  onChange: (state: EditorState) => void;
};

const CustomColorPicker = ({ editorState, onChange }: ColorPickerComponentProps) => {
  const applyColor = useCallback((color: string) => {
    const selection = editorState.getSelection();
    const colorStyle = `color-${color}`;

    // First, remove any existing color styles from current inline style
    let nextEditorState = editorState;
    const currentStyle = editorState.getCurrentInlineStyle();
    currentStyle.forEach((style) => {
      if (style && style.startsWith("color-")) {
        nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, style);
      }
    });

    if (selection.isCollapsed()) {
      // No text selected - toggle the color style so it applies to next typed text
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, colorStyle);
      onChange(nextEditorState);
    } else {
      // Text is selected - apply color to selection
      const currentContent = nextEditorState.getCurrentContent();

      // Remove existing color styles from selection
      let nextContentState = currentContent;
      currentStyle.forEach((style) => {
        if (style && style.startsWith("color-")) {
          nextContentState = Modifier.removeInlineStyle(
            nextContentState,
            selection,
            style
          );
        }
      });

      // Apply new color
      nextContentState = Modifier.applyInlineStyle(
        nextContentState,
        selection,
        colorStyle
      );

      nextEditorState = EditorState.push(
        nextEditorState,
        nextContentState,
        "change-inline-style"
      );
      onChange(nextEditorState);
    }
  }, [editorState, onChange]);

  return (
    <ColorPicker
      presets={colorPresets}
      onChange={(color) => applyColor(color.toHexString())}
    >
      <div className="rdw-option-wrapper ant-icon-toolbar-btn" title="Text Color">
        <BgColorsOutlined style={{ fontSize: 16 }} />
      </div>
    </ColorPicker>
  );
};

const MentionsComponent = ({ onChange, placeHolder, options }: Props) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  // Convert options to the format expected by react-draft-wysiwyg
  const suggestionOptions = useMemo(() => {
    return (options ?? []).map((opt) => ({
      text: opt.label,
      value: opt.label,
      url: opt.value, // Store the ID in url field for extraction
    }));
  }, [options]);

  const extractMentionIds = (mentions: string[]): string[] => {
    const mentionIds = mentions?.map((mention) => {
      const option = options?.find((option) => option.label === mention);
      return option?.value as string;
    });
    return mentionIds ?? [];
  };

  // Function to extract mentions
  const getMentions = () => {
    const rawContent = convertToRaw(editorState.getCurrentContent());
    const mentions: string[] = [];

    rawContent.blocks.forEach((block) => {
      block.entityRanges.forEach((entity) => {
        const entityKey = entity.key;
        const entityData = rawContent.entityMap[entityKey];

        if (entityData && entityData?.type === "MENTION") {
          mentions.push(entityData?.data?.value); // Change based on data structure
        }
      });
    });

    return mentions;
  };

  const onEditorStateChange = function (newEditorState: EditorState) {
    const content = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    const mentions = getMentions();
    const mentionIds = extractMentionIds(mentions);

    setEditorState(newEditorState);

    if (onChange) onChange(content, mentionIds);
  };

  // Custom toolbar component that includes Ant Design ColorPicker
  const ColorPickerButton = () => (
    <CustomColorPicker editorState={editorState} onChange={onEditorStateChange} />
  );

  // Custom style map to handle color styles - includes all preset colors
  const customStyleMap = useMemo(() => {
    const styleMap: Record<string, React.CSSProperties> = {};

    // Add all preset colors to the style map so they're available when typing
    colorPresets[0].colors.forEach((colorValue) => {
      const colorStr = typeof colorValue === "string" ? colorValue : String(colorValue);
      const styleName = `color-${colorStr}`;
      styleMap[styleName] = { color: colorStr };
    });

    // Also extract any custom colors from the content
    const rawContent = convertToRaw(editorState.getCurrentContent());
    rawContent.blocks.forEach((block) => {
      block.inlineStyleRanges.forEach((range) => {
        if (range.style.startsWith("color-")) {
          const color = range.style.replace("color-", "");
          styleMap[range.style] = { color };
        }
      });
    });

    // Include any color from the current inline style (for typing mode)
    const currentStyle = editorState.getCurrentInlineStyle();
    currentStyle.forEach((style) => {
      if (style && style.startsWith("color-")) {
        const color = style.replace("color-", "");
        styleMap[style] = { color };
      }
    });

    return styleMap;
  }, [editorState]);

  return (
    <Editor
      placeholder={placeHolder}
      editorState={editorState}
      toolbarClassName='mentions-toolbar'
      wrapperClassName='mentions-wrapper'
      editorClassName='mentions-editor'
      onEditorStateChange={onEditorStateChange}
      customStyleMap={customStyleMap}
      mention={{
        separator: " ",
        trigger: "@",
        suggestions: suggestionOptions,
      }}
      toolbarCustomButtons={[<ColorPickerButton key="color-picker" />]}
      toolbar={{
        options: ["inline"],
        inline: {
          inDropdown: false,
          className: undefined,
          component: undefined,
          dropdownClassName: undefined,
          options: ["bold", "italic", "underline"],
          bold: { icon: boldIcon, className: "ant-icon-toolbar-btn" },
          italic: { icon: italicIcon, className: "ant-icon-toolbar-btn" },
          underline: { icon: underlineIcon, className: "ant-icon-toolbar-btn" },
        },
      }}
    />
  );
};

export default MentionsComponent;
